// Chakra Imports
import {
  Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink,
  Button, Flex, Text, useColorModeValue
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import AdminNavbarLinks from "./AdminNavbarLinks";

// Match a route path (with :params) against an actual pathname
const matchRoute = (routeFullPath, pathname) => {
  const routeParts = routeFullPath.split('/').filter(Boolean);
  const actualParts = pathname.split('/').filter(Boolean);
  if (routeParts.length !== actualParts.length) return false;
  return routeParts.every((part, i) => part.startsWith(':') || part === actualParts[i]);
};

// Build breadcrumb trail from routes + current pathname
const getBreadcrumbs = (routes, pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];

  const layout = '/' + segments[0]; // '/admin' or '/user'
  const layoutRoutes = routes.filter(r => r.layout === layout);

  // Section path: /admin/worksheets from /admin/worksheets/edit/1
  const sectionPath = segments.length >= 2
    ? '/' + segments.slice(0, 2).join('/')
    : layout;

  const sectionRoute = layoutRoutes.find(r => layout + r.path === sectionPath);
  const currentRoute = layoutRoutes.find(r => matchRoute(layout + r.path, pathname));

  const crumbs = [];

  if (sectionRoute && currentRoute && (layout + sectionRoute.path) !== pathname) {
    crumbs.push({ name: sectionRoute.name, path: layout + sectionRoute.path, isCurrent: false });
  }

  if (currentRoute) {
    crumbs.push({ name: currentRoute.name, path: pathname, isCurrent: true });
  } else if (sectionRoute) {
    crumbs.push({ name: sectionRoute.name, path: sectionPath, isCurrent: true });
  }

  return crumbs;
};

export default function AdminNavbar(props) {
  const [scrolled, setScrolled] = useState(false);
  const {
    variant,
    children,
    fixed,
    secondary,
    brandText,
    routes,
    onOpen,
    ...rest
  } = props;

  let mainText = useColorModeValue("gray.700", "gray.200");
  let secondaryText = useColorModeValue("gray.400", "gray.200");
  let navbarPosition = "absolute";
  let navbarFilter = "none";
  let navbarBackdrop = "blur(21px)";
  let navbarShadow = "none";
  let navbarBg = "none";
  let navbarBorder = "transparent";
  let secondaryMargin = "0px";
  let paddingX = "15px";
  if (props.fixed === true)
    if (scrolled === true) {
      navbarPosition = "fixed";
      navbarShadow = useColorModeValue(
        "0px 7px 23px rgba(0, 0, 0, 0.05)",
        "none"
      );
      navbarBg = useColorModeValue(
        "linear-gradient(112.83deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.8) 110.84%)",
        "linear-gradient(112.83deg, rgba(255, 255, 255, 0.21) 0%, rgba(255, 255, 255, 0) 110.84%)"
      );
      navbarBorder = useColorModeValue("#FFFFFF", "rgba(255, 255, 255, 0.31)");
      navbarFilter = useColorModeValue(
        "none",
        "drop-shadow(0px 7px 23px rgba(0, 0, 0, 0.05))"
      );
    }
  // if (props.secondary) {
  //   navbarBackdrop = "none";
  //   navbarPosition = "absolute";
  //   mainText = "white";
  //   secondaryText = "white";
  //   secondaryMargin = "22px";
  //   paddingX = "30px";
  // }

  const changeNavbar = () => {
    if (window.scrollY > 1) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };
  window.addEventListener("scroll", changeNavbar);

  const history = useHistory();
  const pathname = window.location.pathname;
  const breadcrumbs = routes ? getBreadcrumbs(routes, pathname) : [];
  const pageTitle = breadcrumbs.length > 0
    ? breadcrumbs[breadcrumbs.length - 1].name
    : brandText;

  return (
    <Flex
      position={navbarPosition}
      boxShadow={navbarShadow}
      bg={navbarBg}
      borderColor={navbarBorder}
      filter={navbarFilter}
      backdropFilter={navbarBackdrop}
      borderWidth="1.5px"
      borderStyle="solid"
      transitionDelay="0s, 0s, 0s, 0s"
      transitionDuration=" 0.25s, 0.25s, 0.25s, 0s"
      transition-property="box-shadow, background-color, filter, border"
      transitionTimingFunction="linear, linear, linear, linear"
      alignItems={{ xl: "center" }}
      borderRadius="16px"
      display="flex"
      minH="75px"
      justifyContent={{ xl: "center" }}
      lineHeight="25.6px"
      mx="auto"
      mt={secondaryMargin}
      pb="8px"
      left=""
      right="30px"
      px={{ sm: paddingX, md: "30px" }}
      ps={{ xl: "12px" }}
      pt="8px"
      top="18px"
      w={{ sm: "calc(100vw - 30px)", xl: "calc(100vw - 75px - 275px)" }}
    >
      <Flex
        w="100%"
        flexDirection={{ base: "column", sm: "row" }}
        alignItems={{ xl: "center" }}
      >
        <Box mb={{ sm: "8px", md: "0px" }} px={{ base: "1rem", xl: "0" }}>
          {breadcrumbs.length > 1 && (
            <Breadcrumb
              separator={<ChevronRightIcon color={secondaryText} boxSize="3" />}
              mb="4px"
              fontSize="sm"
            >
              {breadcrumbs.map((crumb, i) => (
                <BreadcrumbItem key={i} isCurrentPage={crumb.isCurrent}>
                  {crumb.isCurrent ? (
                    <BreadcrumbLink color={mainText} fontWeight="semibold" cursor="default" _hover={{}}>
                      {crumb.name}
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbLink
                      as={Link}
                      to={crumb.path}
                      color={secondaryText}
                      _hover={{ color: mainText, textDecoration: "underline" }}
                    >
                      {crumb.name}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              ))}
            </Breadcrumb>
          )}
          <Flex align="center" gap={3}>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              borderRadius="8px"
              onClick={() => history.goBack()}
              px={3}
            >
              ← ย้อนกลับ
            </Button>
            <Text color={mainText} fontWeight="bold" fontSize="lg">
              {pageTitle}
            </Text>
          </Flex>
        </Box>
        <Box ms="auto" w={{ base: "100%", sm: "unset" }}>
          <AdminNavbarLinks
            onOpen={props.onOpen}
            logoText={props.logoText}
            secondary={props.secondary}
            fixed={props.fixed}
            {...rest}
          />
        </Box>
      </Flex>
    </Flex>
  );
}

AdminNavbar.propTypes = {
  brandText: PropTypes.string,
  routes: PropTypes.array,
  variant: PropTypes.string,
  secondary: PropTypes.bool,
  fixed: PropTypes.bool,
  onOpen: PropTypes.func,
};
